import { Editor, Extension } from "@tiptap/core";
import { NodeSelection } from "@tiptap/pm/state";
import StarterKit from "@tiptap/starter-kit";
import Underline from "@tiptap/extension-underline";
import Highlight from "@tiptap/extension-highlight";
import TextAlign from "@tiptap/extension-text-align";
import { Table, TableRow, TableCell, TableHeader } from "@tiptap/extension-table";
import Placeholder from "@tiptap/extension-placeholder";

import { Direction } from "../rich-editor/extensions/direction";
import { runCommand, tableCommand, activeCommands } from "../rich-editor/commands";
import { LiveMath, LiveMathDisplay, focusNextFormula } from "./live-math";
import { toLatex } from "./latex";

// The math document: text and formulas on one page, the formulas written in place - a student's
// working, laid out the way it would be on paper, but typed. Modelled on Mathcha. Published as
// window.examinMathDocument for the Blazor component that draws its toolbar.
//
// What is written is kept on the device under the key the page gives it, like the notebook's
// strokes: it is a working tool beside the activity, not an answer (yet).

class MathDocument {
  readonly editor: Editor;
  private saveTimer: ReturnType<typeof setTimeout> | null = null;
  private activeTimer: ReturnType<typeof setTimeout> | null = null;
  private lastActive = "";

  constructor(
    readonly host: HTMLElement,
    private readonly storageKey: string,
    placeholder: string,
    private readonly dotNetRef: any,
  ) {
    const self = this;

    // Ctrl+M starts a formula in the line, Ctrl+Shift+M one on a line of its own, and a typed $
    // starts one too - the way Mathcha and LaTeX both have it.
    const Shortcuts = Extension.create({
      name: "mathDocumentShortcuts",
      addKeyboardShortcuts() {
        return {
          "Mod-m": () => { self.insertFormula("", false); return true; },
          "Mod-Shift-m": () => { self.insertFormula("", true); return true; },
        };
      },
    });

    this.editor = new Editor({
      element: host,
      extensions: [
        StarterKit.configure({ codeBlock: false, code: false }),
        Underline,
        Highlight,
        TextAlign.configure({ types: ["paragraph", "heading"] }),
        Direction,
        Table.configure({ resizable: false }),
        TableRow,
        TableCell,
        TableHeader,
        LiveMath,
        LiveMathDisplay,
        Placeholder.configure({ placeholder }),
        Shortcuts,
      ],
      content: this.read(),
      editorProps: {
        // Taken at the key, before the character reaches the page: ProseMirror reads typed text
        // back from the page, and redrawing it would pull the caret back out of the new formula.
        handleKeyDown: (_view, event) => {
          if (event.key !== "$" || event.ctrlKey || event.metaKey || event.altKey) return false;
          event.preventDefault();
          self.insertFormula("", false);
          return true;
        },
        // Ctrl+C: the plain-text side of the clipboard is the LaTeX source, so a paste anywhere
        // that takes text gets the formulas as $...$ rather than nothing.
        clipboardTextSerializer: (slice) => toLatex(slice.content),
        // What arrives without a key - an on-screen keyboard, a dictation.
        handleTextInput: (_view, _from, _to, text) => {
          if (text !== "$") return false;
          setTimeout(() => self.insertFormula("", false));
          return true;
        },
      },
      onUpdate: () => { this.save(); this.publishActive(); },
      onSelectionUpdate: () => this.publishActive(),
    });
    this.publishActive(true);
  }

  // ── Keeping it ─────────────────────────────────────────────────────────────────────────────
  private read(): string {
    if (!this.storageKey) return "";
    try { return localStorage.getItem(this.storageKey) || ""; } catch { return ""; }
  }

  private save(): void {
    if (!this.storageKey) return;
    if (this.saveTimer) clearTimeout(this.saveTimer);
    this.saveTimer = setTimeout(() => this.flush(), 400);
  }

  flush(): void {
    if (!this.storageKey) return;
    if (this.saveTimer) { clearTimeout(this.saveTimer); this.saveTimer = null; }
    try {
      if (this.editor.isEmpty) localStorage.removeItem(this.storageKey);
      else localStorage.setItem(this.storageKey, this.editor.getHTML());
    } catch { /* storage full or refused - nothing else holds it */ }
  }

  // The toolbar's pressed buttons, sent only when they change: a caret moving through a line
  // fires selection events all the time, and each send is a round trip.
  private publishActive(immediate = false): void {
    if (this.activeTimer) clearTimeout(this.activeTimer);
    const send = () => {
      const keys = activeCommands(this.editor);
      const signature = keys.join("|");
      if (signature === this.lastActive) return;
      this.lastActive = signature;
      this.dotNetRef?.invokeMethodAsync("OnActiveChanged", keys);
    };
    if (immediate) send(); else this.activeTimer = setTimeout(send, 120);
  }

  // ── Formulas ───────────────────────────────────────────────────────────────────────────────
  /** The formula the caret is in, if it is in one of this document's. */
  private focusedField(): any {
    const active = document.activeElement as any;
    return active?.tagName === "MATH-FIELD" && this.host.contains(active) ? active : null;
  }

  /// A symbol or a template goes into the formula being written; with the caret in the text, it
  /// starts a new formula holding it. A template's empty boxes are where the caret goes next.
  insertFormula(latex: string, display: boolean): void {
    const field = this.focusedField();
    if (field && latex) {
      field.insert(latex, { format: "latex", selectionMode: "placeholder", focus: true });
      field.focus();
      return;
    }
    focusNextFormula();
    this.editor.chain().focus().insertContent({ type: display ? "liveMathDisplay" : "liveMath", attrs: { latex } }).run();
    if (latex.includes("\\placeholder")) {
      requestAnimationFrame(() => requestAnimationFrame(() => {
        const next = this.focusedField();
        next?.executeCommand?.("moveToMathfieldStart");
        next?.executeCommand?.("moveToNextPlaceholder");
      }));
    }
  }

  /// The selection - or, with nothing selected, the whole page - onto the clipboard. "answer" puts
  /// it there as our own editor's HTML (the formulas as <math-field data-latex>, which the answer
  /// editor takes as formulas) with the LaTeX alongside as text; "latex" puts only the source.
  async copy(mode: string): Promise<boolean> {
    const { state, view } = this.editor;
    // A range the student marked is what they meant; a caret, or the formula they were just
    // writing in (selected by being in it), is not a choice - the whole page is.
    const marked = !state.selection.empty && !(state.selection instanceof NodeSelection);
    const slice = marked ? state.selection.content() : state.doc.slice(0, state.doc.content.size);
    const latex = toLatex(slice.content);
    let html = "";
    if (mode !== "latex") {
      const { dom } = (view as any).serializeForClipboard(slice);
      dom.querySelectorAll("[data-pm-slice]").forEach((el: Element) => el.removeAttribute("data-pm-slice"));
      html = displayAsParagraphs(dom.innerHTML);
    }
    return writeClipboard(html, latex);
  }

  command(command: string, value?: string): boolean {
    if (command === "table:delete-table") return this.editor.chain().focus().deleteTable().run();
    if (command.startsWith("table:")) return tableCommand(this.editor, command.slice(6));
    if (command === "insertTable")
      return this.editor.chain().focus().insertTable({ rows: 3, cols: 3, withHeaderRow: false }).run();
    if (command === "dir") return (this.editor.commands as any).setDirection(value as any);
    if (command === "clear") return this.editor.chain().focus().clearContent(true).run();
    return runCommand(this.editor, command, value);
  }

  dispose(): void {
    this.flush();
    if (this.activeTimer) clearTimeout(this.activeTimer);
    this.editor.destroy();
  }
}

// A formula on its own line is our own block; an editor that does not know it gets a centred
// paragraph holding the same formula.
function displayAsParagraphs(html: string): string {
  return html.replace(/<div data-math-display="">([\s\S]*?)<\/div>/g, '<p style="text-align: center">$1</p>');
}

async function writeClipboard(html: string, text: string): Promise<boolean> {
  try {
    const Item = (window as any).ClipboardItem;
    if (navigator.clipboard?.write && Item) {
      const parts: Record<string, Blob> = { "text/plain": new Blob([text], { type: "text/plain" }) };
      if (html) parts["text/html"] = new Blob([html], { type: "text/html" });
      await navigator.clipboard.write([new Item(parts)]);
      return true;
    }
  } catch { /* refused - an insecure page, a lockdown browser; the copy event below still works */ }
  let done = false;
  const onCopy = (event: ClipboardEvent) => {
    event.clipboardData?.setData("text/plain", text);
    if (html) event.clipboardData?.setData("text/html", html);
    event.preventDefault();
    done = true;
  };
  document.addEventListener("copy", onCopy);
  try { document.execCommand("copy"); } finally { document.removeEventListener("copy", onCopy); }
  return done;
}

const instances = new Map<string, MathDocument>();

const examinMathDocument = {
  async init(hostId: string, storageKey: string, placeholder: string, dotNetRef: any): Promise<void> {
    examinMathDocument.dispose(hostId);
    const host = document.getElementById(hostId);
    if (!host) return;
    await customElements.whenDefined("math-field");
    instances.set(hostId, new MathDocument(host, storageKey || "", placeholder || "", dotNetRef));
  },

  insertFormula(hostId: string, latex: string, display: boolean): void {
    instances.get(hostId)?.insertFormula(latex || "", Boolean(display));
  },

  command(hostId: string, command: string, value?: string): boolean {
    return instances.get(hostId)?.command(command, value) ?? false;
  },

  copy(hostId: string, mode: string): Promise<boolean> {
    return instances.get(hostId)?.copy(mode) ?? Promise.resolve(false);
  },

  /** MathLive's on-screen keyboard, for a tablet or anyone who would rather tap. */
  toggleKeyboard(): boolean {
    const keyboard = (window as any).mathVirtualKeyboard;
    if (!keyboard) return false;
    if (keyboard.visible) keyboard.hide(); else keyboard.show();
    return keyboard.visible;
  },

  dispose(hostId: string): void {
    const doc = instances.get(hostId);
    if (!doc) return;
    doc.dispose();
    instances.delete(hostId);
  },
};

(window as any).examinMathDocument = examinMathDocument;
